import { useEffect, useState } from "react";
import type { ReaderWorkspace } from "../../../reader/reader-workspace";
import { ReaderOverlayPanel } from "./reader-overlay-panel";
import { SettingStepper } from "./reader-settings-controls";
import {
  getReaderDevice,
  getReaderMarginPresets,
  getReaderTypographyPresets,
  getSelectedMarginPreset,
  getSelectedTypographyPreset,
  type ReaderDevice,
  type ReaderMarginPadding,
  type ReaderTypographyPresetId,
  type ReaderTypographySettings,
} from "./reader-settings-presets";
import {
  FontSettings,
  MarginSettings,
  ThemeSettings,
  TypographySettings,
} from "./reader-settings-sections";

export function ReaderSettingsPanel({
  workspace,
  fullscreen,
  fullscreenSupported,
  onClose,
  onFullscreenToggle,
}: {
  workspace: ReaderWorkspace;
  fullscreen: boolean;
  fullscreenSupported: boolean;
  onClose: () => void;
  onFullscreenToggle: () => void;
}) {
  const preference = workspace.preference;
  const [device, setDevice] = useState<ReaderDevice>(() => getReaderDevice(window.innerWidth));
  const [typographyExpanded, setTypographyExpanded] = useState(false);
  const [typographyPresetOverride, setTypographyPresetOverride] = useState<"custom" | null>(null);
  const [marginExpanded, setMarginExpanded] = useState(false);
  const [marginPresetOverride, setMarginPresetOverride] = useState<"custom" | null>(null);
  const typographyPresets = getReaderTypographyPresets(device);
  const marginPresets = getReaderMarginPresets(device);
  const selectedTypographyPreset = typographyPresetOverride ?? getSelectedTypographyPreset(preference, typographyPresets);
  const selectedMarginPreset = marginPresetOverride ?? getSelectedMarginPreset(preference, marginPresets);

  function handleTypographyPresetChange(value: ReaderTypographyPresetId | "custom") {
    if (value === "custom") {
      setTypographyPresetOverride("custom");
      setTypographyExpanded(true);
      return;
    }

    setTypographyPresetOverride(null);
    setTypographyExpanded(false);
    // 排版预设只控制字距行距段距 缩进由展开项单独控制
    void workspace.handlePreferenceChange(typographyPresets[value].settings);
  }

  function handleCustomTypographyChange(patch: Partial<ReaderTypographySettings>) {
    setTypographyPresetOverride("custom");
    void workspace.handlePreferenceChange(patch);
  }

  function handleCustomPaddingChange(patch: Partial<ReaderMarginPadding>) {
    setMarginPresetOverride("custom");
    void workspace.handlePreferenceChange(patch);
  }

  useEffect(() => {
    const syncDevice = () => setDevice(getReaderDevice(window.innerWidth));

    window.addEventListener("resize", syncDevice);
    return () => window.removeEventListener("resize", syncDevice);
  }, []);

  return (
    <ReaderOverlayPanel title="阅读设置" contentClassName="space-y-6 p-4" onClose={onClose}>
      <ThemeSettings
        workspace={workspace}
        fullscreen={fullscreen}
        fullscreenSupported={fullscreenSupported}
        onFullscreenToggle={onFullscreenToggle}
      />

      <SettingStepper
        label="字号"
        value={`${preference.fontSize}px`}
        onLess={() => void workspace.handlePreferenceChange({ fontSize: preference.fontSize - 1 })}
        onMore={() => void workspace.handlePreferenceChange({ fontSize: preference.fontSize + 1 })}
      />

      <FontSettings workspace={workspace} />

      <TypographySettings
        preference={preference}
        presets={typographyPresets}
        selectedPreset={selectedTypographyPreset}
        expanded={typographyExpanded}
        onToggleExpanded={() => setTypographyExpanded(current => !current)}
        onPresetChange={handleTypographyPresetChange}
        onCustomTypographyChange={handleCustomTypographyChange}
        onIndentChange={value => void workspace.handlePreferenceChange({ textIndent: value })}
        onTextAlignChange={value => void workspace.handlePreferenceChange({ textAlign: value })}
        onTextAlignLastChange={value => void workspace.handlePreferenceChange({ textAlignLast: value })}
      />

      <MarginSettings
        preference={preference}
        presets={marginPresets}
        selectedPreset={selectedMarginPreset}
        expanded={marginExpanded}
        onToggleExpanded={() => setMarginExpanded(current => !current)}
        onPresetChange={value => {
          if (value === "custom") {
            setMarginPresetOverride("custom");
            setMarginExpanded(true);
            return;
          }

          setMarginPresetOverride(null);
          setMarginExpanded(false);
          void workspace.handlePreferenceChange(marginPresets[value].padding);
        }}
        onCustomPaddingChange={handleCustomPaddingChange}
      />
    </ReaderOverlayPanel>
  );
}
